import { useTheme } from "next-themes";
import dxcLogoDark from "@/assets/logos/DXC-1-Color-Dark.svg";
import dxcLogoFullColor from "@/assets/logos/DXC-Full-Color.svg";

/**
 * Official DXC Technology brand mark, sourced verbatim from the Jan 2026
 * brand pack:
 *   - Light theme → `Brand Mark/1 Color/RGB/DXC-1-Color-Dark.svg`
 *     (dark-ink wordmark, reads as solid against the linen background).
 *   - Dark theme  → `Brand Mark/Full Color/RGB/DXC-Full-Color.svg`
 *     (multi-colour DXC mark — the brand "hero" treatment, reads as
 *     vivid against the Midnight Blue panel without needing an
 *     inversion or blend trick).
 *
 * The HUD header sits on a glass panel over the mission background, and
 * the brand rule states the one-colour logo is the correct choice "when
 * overlaid on photography or video" (Brand Central → DXC Logo). The
 * Full Color mark is used here because the panel beneath it is a
 * solid Midnight Blue, not photography, so the colour treatment carries.
 *
 * Brand rules enforced in code:
 *   - Minimum digital width 100 px. The 860 × 240 source is ≈ 3.583:1,
 *     so the minimum compliant height is 28 px. Any shorter request is
 *     clamped upward rather than silently violating the rule.
 *   - Aspect ratio preserved — width is always `auto`.
 *   - Clear-space (half the logo height) is the caller's responsibility.
 */

const LOGO_ASPECT = 860 / 240;
const MIN_DIGITAL_WIDTH_PX = 100;
const MIN_HEIGHT_PX = Math.ceil(MIN_DIGITAL_WIDTH_PX / LOGO_ASPECT);

type DxcLogoProps = {
  height?: number;
  className?: string;
};

export function DxcLogo({ height = MIN_HEIGHT_PX, className }: DxcLogoProps) {
  const { resolvedTheme } = useTheme();
  const src = resolvedTheme === "light" ? dxcLogoDark : dxcLogoFullColor;
  const renderedHeight = Math.max(height, MIN_HEIGHT_PX);
  return (
    <img
      src={src}
      alt="DXC Technology"
      height={renderedHeight}
      style={{ height: renderedHeight, width: "auto", display: "block" }}
      className={className}
    />
  );
}
